import { produce } from 'immer'
import * as R from 'remeda'
import { match } from 'ts-pattern'
import { z } from 'zod'

import {
  dataDisplayTypes,
  exists,
  type GroupIndex,
  groupIndexes,
  type NineNumber,
  resultViewerTypes,
  toGroup,
  toNineSegment,
} from '~/utils'

import { type SequentialSortColumnKey } from '../../../utils'
import { type RestoringParams } from '../types'

const columnKeyLinearQuestionsSequentialMap: Record<
  number | 'all',
  SequentialSortColumnKey
> = {
  all: 'all',
  ...R.mapToObj(groupIndexes, (groupIndex) => [
    groupIndex - 1,
    toGroup(groupIndex),
  ]),
}

const columnKeyLinearQuestionIndividualMap: Record<
  number | 'all',
  SequentialSortColumnKey
> = {
  all: 'all',
  ...R.mapToObj(groupIndexes, (groupIndex) => [
    groupIndex - 1,
    toGroup(groupIndex),
  ]),
}

export const schema1 = z
  .object({
    version: z.undefined(),
    restorer: z.object({
      params: z.object({
        brandGroupId: z.number(),
        questionIds: z.array(z.string()),
        selectedGroupIndexMap: z.record(
          z.string(),
          z.record(z.string(), z.number()),
        ),
      }),
      view: z.object({
        dataDisplayType: z.enum(dataDisplayTypes),
        resultViewerType: z.enum(resultViewerTypes),
        hiddenDisplaySwitchingTargets: z.array(z.string()),
        sortInfoLinearQuestionIndividual: z.object({
          columnKey: z.union([z.literal('all'), z.number()]),
          ascending: z.boolean(),
        }),
        sortInfoLinearQuestionsSequential: z.object({
          columnKey: z.union([z.literal('all'), z.number()]),
          ascending: z.boolean(),
        }),
        sortInfoMatrixQuestion: z.object({
          columnKey: z.string(),
          ascending: z.boolean(),
        }),
      }),
    }),
    scroller: z.object({ hash: z.string() }),
  })
  .deepPartial()
  .transform((value) =>
    produce<Partial<RestoringParams>>({}, (draft) => {
      draft.brandGroupId = value.restorer?.params?.brandGroupId

      draft.dataDisplayType = value.restorer?.view?.dataDisplayType
      draft.groupInfos = R.entries(
        value.restorer?.params?.selectedGroupIndexMap ?? {},
      ).flatMap(([brandNumber, segmentNumberGroupIndexMap]) =>
        R.entries(segmentNumberGroupIndexMap).map(
          ([segmentNumber, groupIndex]) => ({
            brandNumber: Number(brandNumber),
            nineSegment: toNineSegment(Number(segmentNumber) as NineNumber),
            group: toGroup((groupIndex + 1) as GroupIndex),
          }),
        ),
      )
      draft.resultViewerType = value.restorer?.view?.resultViewerType
      draft.questionIds = value.restorer?.params?.questionIds

      const sortInfoLinearQuestionsSequential =
        value.restorer?.view?.sortInfoLinearQuestionsSequential
      const columnKeyLinearQuestionsSequential =
        sortInfoLinearQuestionsSequential?.columnKey
      const ascendingLinearQuestionsSequential =
        sortInfoLinearQuestionsSequential?.ascending
      if (
        exists(columnKeyLinearQuestionsSequential) &&
        exists(ascendingLinearQuestionsSequential)
      ) {
        draft.sequentialSortValue = {
          columnKey:
            columnKeyLinearQuestionsSequentialMap[
              columnKeyLinearQuestionsSequential
            ],
          ascending: ascendingLinearQuestionsSequential,
        }
      }

      const sortInfoLinearQuestionIndividual =
        value.restorer?.view?.sortInfoLinearQuestionIndividual
      const columnKeyLinearQuestionIndividual =
        sortInfoLinearQuestionIndividual?.columnKey
      const ascendingLinearQuestionIndividual =
        sortInfoLinearQuestionIndividual?.ascending
      if (
        exists(columnKeyLinearQuestionIndividual) &&
        exists(ascendingLinearQuestionIndividual)
      ) {
        draft.individualSortValue = {
          columnKey:
            columnKeyLinearQuestionIndividualMap[
              columnKeyLinearQuestionIndividual
            ],
          ascending: ascendingLinearQuestionIndividual,
        }
      }

      const sortInfoMatrixQuestion =
        value.restorer?.view?.sortInfoMatrixQuestion
      const columnKeyMatrixQuestion = sortInfoMatrixQuestion?.columnKey
      const ascendingMatrixQuestion = sortInfoMatrixQuestion?.ascending
      if (exists(columnKeyMatrixQuestion) && exists(ascendingMatrixQuestion)) {
        draft.matrixSortValue = {
          columnKey: columnKeyMatrixQuestion,
          ascending: ascendingMatrixQuestion,
        }
      }

      draft.hiddenRowIds = value.restorer?.view?.hiddenDisplaySwitchingTargets

      draft.hash = match(value.scroller?.hash)
        .with(undefined, () => undefined)
        .with('linear-answers-table', () => 'sequential')
        .when(
          (hash) => hash.startsWith('linear-answers-chart-table-'),
          (hash) =>
            hash.replace('linear-answers-chart-table-', 'individual-table-'),
        )
        .when(
          (hash) => hash.startsWith('linear-answers-chart-chart-'),
          (hash) =>
            hash.replace('linear-answers-chart-chart-', 'individual-chart-'),
        )
        .when(
          (hash) => hash.startsWith('matrix-answers-table-'),
          (hash) => {
            const replaced = hash.replace('matrix-answers-table-', 'matrix-')
            if (replaced.endsWith('all')) {
              return replaced
            }
            const splitted = replaced.split('-')
            const last = toGroup((Number(splitted.slice(-1)) + 1) as GroupIndex)

            return `${splitted.slice(0, -1).join('-')}-${last}`
          },
        )
        .otherwise(() => undefined)
    }),
  )
